import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Package, Clock, CheckCircle2, Users, Settings, Boxes,
  Bell, LogOut, Menu, X, Search, ShieldCheck, BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "All Orders", icon: Package },
  { to: "/admin/orders/in-progress", label: "In Progress", icon: Clock },
  { to: "/admin/orders/completed", label: "Completed", icon: CheckCircle2 },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/packages", label: "Packages", icon: Boxes },
  { to: "/admin/content", label: "Content Studio", icon: BookOpen },
  { to: "/admin/team", label: "Team", icon: ShieldCheck },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setNotifications(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 15000);
    return () => clearInterval(t);
  }, []);

  const toggleNotif = async () => {
    setNotifOpen((v) => !v);
    if (!notifOpen && unread > 0) {
      try {
        await api.patch("/admin/notifications/read");
        setUnread(0);
      } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky lg:top-0 left-0 h-screen w-64 bg-[#0f1b2d] text-white z-40 transform transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 flex flex-col`}
      >
        <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
          <Link to="/admin" className="font-display font-extrabold text-lg">
            Schengen<span className="text-[#60a5fa]">File</span>
          </Link>
          <button className="lg:hidden text-white/70" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#2563eb] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <Link to="/" className="text-xs text-white/60 hover:text-white block mb-3">← Back to site</Link>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-[#e2e8f0] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-[#0f1b2d]" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden md:flex items-center gap-2 bg-[#f8fafc] border border-[#e2e8f0] rounded-md px-3 py-1.5 text-sm w-72">
              <Search className="w-4 h-4 text-[#94a3b8]" />
              <span className="text-[#94a3b8] text-xs">Press / in any orders page to search</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Bell */}
            <div className="relative">
              <button
                onClick={toggleNotif}
                data-testid="admin-bell"
                className="relative w-9 h-9 rounded-md grid place-items-center hover:bg-[#f1f5f9]"
              >
                <Bell className="w-5 h-5 text-[#0f1b2d]" />
                {unread > 0 && (
                  <span data-testid="bell-badge" className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 bg-white border border-[#e2e8f0] rounded-lg shadow-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#e2e8f0] text-xs font-semibold uppercase tracking-wider text-[#64748b]">
                    Notifications
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-[#475569] text-center">No notifications yet.</div>
                    ) : (
                      notifications.map((n) => (
                        <Link
                          key={n.id}
                          to="/admin/orders"
                          onClick={() => setNotifOpen(false)}
                          className="block px-4 py-3 hover:bg-[#f8fafc] border-b border-[#e2e8f0]"
                        >
                          <div className="text-sm text-[#0f1b2d]">
                            New order from <span className="font-semibold">{n.customer_name}</span> — {n.destination_country}
                          </div>
                          <div className="text-xs text-[#475569] mt-0.5">{n.package_name} · ${Math.round(n.amount)}</div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-[#0f1b2d]">{user?.name}</div>
              <div className="text-xs text-[#64748b]">{user?.email}</div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-8">
          <Outlet context={{ refreshNotifications: loadNotifs }} />
        </main>
      </div>
    </div>
  );
}
